import { Middleware } from 'redux'
import _ from 'lodash'
import { FormDispatch, FormRootState } from './formStore'
import { selectConceptStatuses } from './selectConceptStatus2'
import { shallowEqual } from 'react-redux'
import { ImportWriteMode } from '@fusion-advantage/backcompat'
import { createAction, createSelector } from '@reduxjs/toolkit'
import { updateDiagnosisItemsFromInputs } from './diagnosisReviewSlice'
import { selectForm } from './selectForm'

// run on encounter open to ensure brand new encounters get an initial table
// with dx history present
export const createDiagnosisItemsFromInputs = createAction<void>('diagnosisReview/createDiagnosisItemsFromInputs')

const selectInputCids = createSelector(
	(state: FormRootState) => selectForm(state).concepts,
	(concepts) => {
		const cids: {[cid: string]: boolean} = {}
		for(let cid in concepts) {
			const concept = concepts[cid]
			if(concept.isDx) {
				cids[cid] = true
			}
		}
		const dxCids = Object.keys(cids)
		return {
			inputCids: [
				// now checked differently below...
				// ClaimHistoryConcept.cid,
				// PrescriptionReviewConcept.cid,
				// OncologyReviewConcept.cid,
				// UlcerReviewConcept.cid,
				// NeurologicWeaknessConcept.cid,
				...dxCids,
			],
			dxCids,
		}
	}
)

// This middleware observes when an input to the Diagnosis Review table occurs
// and adds/updates/removes DiagnosisReviewItems accordingly.
// Inputs are, as accounted for above and in updateDiagnosisItemsFromForm:
//   * Dx History / Claims History (provided by database)
//   * Indications from the prescriptions table
//   * Diagnosis checkboxes on the form
// This will run on any change to the above, including when loadExport loads
// the initial concept statuses upon opening a form.  This is best to always
// show the most accurate information to the clinician given the input data.
// For example, if the Claims History data had been amended (although it is
// not supposed to), we would want the table to reflect that for complete
// accuracy.  However, we also want to be extremely sensitive to not assert
// diagnoses that the clinician has not actually Asserted.
//   * Additional claims history items ("Dx History") may appear, but not with
//     automatically Asserted status.
//   * Indications don't occur from calculations or other side effects
//     currently.
//   * If a rogue calculation runs and sets a Dx check on the form to true, the
//     only status it can be according to current logic is Asserted (to prevent
//     clinicians from checking the checkbox and then setting its status to
//     History Of or Indeterminate, which creates a conflict in the record).
//     Calculations running when we don't want them to presents the biggest
//     risk in this sense.
export const diagnosisReviewInputMw: Middleware<{}, FormRootState> = store => {
	const dispatch = store.dispatch as FormDispatch
	return next => action => {
		const prevState = store.getState()
		const result = next(action)
		const nextState = store.getState()
		const { inputCids, dxCids } = selectInputCids(nextState)
		// this is a new encounter, so execute initial table draw
		// regardless of writeMode
		if(createDiagnosisItemsFromInputs.match(action) && nextState.diagnosisReview.models.length === 0) {
			dispatch(updateDiagnosisItemsFromInputs(dxCids))
		}
		switch(nextState.render.form?.writeMode) {
			case ImportWriteMode.WriteAll:
			// In the future, when the audit team is no longer responsible for
			// performing certain workarounds on the form, we should drop this
			// WriteAuditReview case and permit the below code to run on
			// WriteAll mode only for additional safety.
			case ImportWriteMode.WriteAuditReview:
				// if dxHistory, form dx, or rx indication changes, update the table
				const prevStatuses = selectConceptStatuses(prevState, inputCids)
				const nextStatuses = selectConceptStatuses(nextState, inputCids)
				if(
					prevState.diagnosisReview.adhocDx !== nextState.diagnosisReview.adhocDx ||
					prevState.formModel.claimHistory !== nextState.formModel.claimHistory ||
					prevState.drug.models !== nextState.drug.models ||
					prevState.formModel.oncologyReview !== nextState.formModel.oncologyReview ||
					prevState.formModel.ulcerReview !== nextState.formModel.ulcerReview ||
					prevState.neurologicWeaknessReview.models !== nextState.neurologicWeaknessReview.models ||
					(!shallowEqual(prevStatuses, nextStatuses))
				) {
					dispatch(updateDiagnosisItemsFromInputs(dxCids))
				}
		}
		return result
	}
}
